import React, { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, Pagination } from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { fetchNewsletters, subscribeNewsletter } from '../api';
import { useNavigate } from 'react-router-dom';
import useWindowDimensions from '../../../../Reusables/VisualAndAccord/Hooks/useWindowDimensions';
import moment from 'moment';
import PartnersSection from '../../Home/components/PartnersSection';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow:
      '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)',
  },
  headerSection: {
    padding: theme.spacing(3),
    paddingBottom: 0,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
      paddingBottom: 0,
    },
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(2),
    },
  },
  titleSubtitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#1E293B',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  },
  subtitle: {
    fontSize: '16px',
    color: '#64748B',
    lineHeight: 1.6,
  },
  subscribeButton: {
    backgroundColor: '#10B981',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'uppercase',
    padding: theme.spacing(1.5, 4),
    minWidth: 'auto',
    color: 'white',
    '&:hover': {
      backgroundColor: '#059669',
    },
    [theme.breakpoints.down('sm')]: {
      alignSelf: 'flex-start',
      marginTop: theme.spacing(1),
      padding: theme.spacing(1.25, 2),
    },
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: 'rgba(226, 232, 240, 0.8)',
  },
  contentSection: {
    padding: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  newsletterList: {
    display: 'flex',
    flexDirection: 'column',
  },
  newsletterItem: {
    padding: theme.spacing(4, 0),
    '&:first-child': {
      paddingTop: 0,
    },
    '&:not(:last-child)': {
      borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 0),
    },
  },
  contentWrapper: {
    display: 'flex',
    gap: theme.spacing(4),
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  imageContainer: {
    flex: '0 0 42%',
    height: '205px',
    borderRadius: '12px',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 auto',
      width: '100%',
      height: 'auto',
    },
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
    },
  },
  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    minHeight: '205px',
    [theme.breakpoints.down('sm')]: {
      minHeight: 'auto',
    },
  },
  date: {
    color: '#4361EE',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  storiesTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1E293B',
    lineHeight: 1.4,
    marginBottom: theme.spacing(1.5),
  },
  headlinesList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  headlineItem: {
    display: 'flex',
    alignItems: 'flex-start',
    color: '#64748B',
    fontSize: '16px',
    lineHeight: 1.6,
    marginBottom: theme.spacing(1),
    '&:before': {
      content: '"\u2022"',
      color: '#94A3B8',
      marginRight: theme.spacing(1.5),
      fontSize: '16px',
    },
  },
  description: {
    color: '#64748B',
    fontSize: '16px',
    lineHeight: 1.6,
    marginTop: theme.spacing(1.5),
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 'auto',
  },
  footerText: {
    color: '#1E293B',
    fontSize: '16px',
  },
  readMore: {
    color: '#4361EE',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  readMoreText: {
    fontSize: '16px',
    fontWeight: 500,
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4, 0),
    '& .MuiPaginationItem-root': {
      color: '#64748B',
      '&.Mui-selected': {
        backgroundColor: '#4361EE',
        color: 'white',
        '&:hover': {
          backgroundColor: '#3651D4',
        },
      },
    },
  },
}));

const NewsletterContent = () => {
  const classes = useStyles();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');
  const [subscribeSuccessOpen, setSubscribeSuccessOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const items = await fetchNewsletters();
      if (items && items.length) {
        setList(items);
      } else {
        setList([]);
      }
      setLoading(false);
    })();
  }, []);

  const formatDate = (dateString) => {
    return moment(dateString).format('MMMM D, YYYY');
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = list.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(list.length / itemsPerPage);

  return (
    <div className={classes.root}>
      {/* Header Section */}
      <div className={classes.headerSection}>
        <div className={classes.headerContent}>
          <div className={classes.titleSubtitleWrapper}>
            <Typography className={classes.title}>Plootus Weekly</Typography>
            <Typography className={classes.subtitle}>
              Your weekly dose of financial tips, strategies, and
              insights
            </Typography>
          </div>
          <Button
            className={classes.subscribeButton}
            onClick={() => setSubscribeOpen(true)}
          >
            SUBSCRIBE
          </Button>
        </div>
        <div className={classes.divider} />
      </div>

      {/* Partners Section */}
      <PartnersSection titleDisabled={true} rootPadding="24px 0" />
      <div className={classes.divider} />

      {/* Newsletter Content */}
      <div className={classes.contentSection}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </div>
        ) : (
          <div className={classes.newsletterList}>
            {currentItems.map((item, idx) => (
              <div key={item.id || idx} className={classes.newsletterItem}>
                <div className={classes.contentWrapper}>
                  <div className={classes.imageContainer}>
                    {item?.image ? (
                      <img src={item.image} alt="Newsletter" className={classes.image} />
                    ) : null}
                  </div>
                  <div className={classes.contentContainer}>
                    {item?.date ? (
                      <Typography className={classes.date}>{formatDate(item.date)}</Typography>
                    ) : null}
                    <Typography variant="h3" className={classes.storiesTitle}>
                      {item?.heading || 'Top Stories This Week'}
                    </Typography>
                    {Array.isArray(item?.headlines) && item.headlines.length ? (
                      <ul className={classes.headlinesList}>
                        {item.headlines.map((headline, i) => (
                          <li key={i} className={classes.headlineItem}>
                            {headline}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {item?.description ? (
                      <div
                        className={classes.description}
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            const raw = item.description || '';
                            if (typeof raw === 'string' && /<[^>]+>/.test(raw)) return raw;
                            if (raw) return String(raw).replace(/\n/g, '<br />');
                            return '';
                          })(),
                        }}
                      />
                    ) : null}
                    <div className={classes.footer}>
                      <Typography className={classes.footerText}>
                        Read more in this week's newsletter!
                      </Typography>
                      <div
                        className={classes.readMore}
                        onClick={() => {
                          const id = item?.id;
                          if (id) navigate(`/newsletters/${item.slug || id}`);
                        }}
                      >
                        <Typography className={classes.readMoreText}>
                          Read More
                        </Typography>
                        <ArrowForwardIcon fontSize="small" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {totalPages > 1 && (
              <div className={classes.paginationContainer}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                />
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={subscribeOpen} onClose={() => !subscribeLoading && setSubscribeOpen(false)}>
        <DialogTitle>Subscribe to Plootus Weekly</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={subscribeEmail}
            onChange={(e) => setSubscribeEmail(e.target.value)}
            error={!!subscribeError}
            helperText={subscribeError || 'We will send newsletters to this email'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscribeOpen(false)} disabled={subscribeLoading}>Cancel</Button>
          <Button
            onClick={async () => {
              setSubscribeError('');
              const email = subscribeEmail.trim();
              if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                setSubscribeError('Enter a valid email address');
                return;
              }
              setSubscribeLoading(true);
              const ok = await subscribeNewsletter(email);
              setSubscribeLoading(false);
              if (ok) {
                setSubscribeOpen(false);
                setSubscribeEmail('');
                setSubscribeSuccessOpen(true);
              } else {
                setSubscribeError('Subscription failed. Please try again.');
              }
            }}
            disabled={subscribeLoading}
          >
            {subscribeLoading ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={subscribeSuccessOpen} autoHideDuration={3000} onClose={() => setSubscribeSuccessOpen(false)}>
        <Alert onClose={() => setSubscribeSuccessOpen(false)} severity="success" sx={{ width: '100%' }}>
          You are subscribed to Plootus newsletter.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default NewsletterContent;
